export interface DashboardTranslationEntry {
  message: string;
  description: string;
}

const descriptions = {
  accountsDescription: "Description for the dashboard account-balance card",
  allWidgetsHidden: "Empty state when every dashboard module is hidden",
  chartView: "Accessible label for switching a financial card to chart view",
  customize: "Button and panel title for dashboard customization",
  customizeDescription: "Description for dashboard customization controls",
  financialPosition: "Heading for net worth and account balances",
  financialPositionDescription:
    "Description for the financial position section",
  fromPreviousMonth: "Label for a change compared with the previous month",
  ledgerNotes: "Label for the ledger README dashboard module",
  moneyIn: "Title for monthly income flow",
  moneyMovement: "Heading for monthly income and spending",
  moneyMovementDescription: "Description for the money movement section",
  moneyOut: "Title for monthly expense flow",
  moveDown: "Accessible label for moving a dashboard module down",
  moveUp: "Accessible label for moving a dashboard module up",
  netWorthDescription: "Description for the net-worth trend card",
  noMovement: "Empty state for a month without income or expenses",
  noRecentActivity: "Empty state for recent transactions",
  partialPeriod: "Short label indicating that a month is incomplete",
  previousThreeMonthsAverage: "Label for the prior-three-month average",
  recentActivity: "Heading for recent journal transactions",
  recentActivityCardDescription:
    "Description inside the recent transaction card",
  recentActivityDescription: "Description for the recent activity section",
  recentActivityError: "Error state for recent transactions",
  resetLayout: "Button to restore the default dashboard layout",
  showWidget: "Accessible label for showing a dashboard module",
  tableView: "Accessible label for switching a financial card to table view",
  topSources: "Heading for the largest income accounts",
  topSpending: "Heading for the largest expense accounts",
  transactionCleared: "Status label for a cleared transaction",
  transactionMultiple:
    "Label for a transaction without one representative amount",
  transactionOther: "Status label for another transaction flag",
  transactionPending: "Status label for a pending transaction",
  transactionTransfer: "Label for a transfer between balance-sheet accounts",
  unknownTransaction:
    "Fallback title for a transaction without payee or narration",
  viewAll: "Link label to open the complete related report",
  viewAllAccounts: "Accessible link label to open the account list",
} as const;

type DashboardTranslationKey = keyof typeof descriptions;
type DashboardLanguage =
  | "bg"
  | "ca"
  | "de"
  | "en"
  | "es"
  | "fa"
  | "fr"
  | "ja"
  | "ko"
  | "nl"
  | "pt"
  | "ru"
  | "sk"
  | "uk"
  | "zh";

const messages: Record<
  DashboardLanguage,
  Record<DashboardTranslationKey, string>
> = {
  en: {
    accountsDescription: "Current asset and liability account balances",
    allWidgetsHidden:
      "All dashboard modules are hidden. Customize the dashboard to restore one.",
    chartView: "Show chart",
    customize: "Customize",
    customizeDescription:
      "Choose which dashboard modules appear and arrange them for this ledger.",
    financialPosition: "Financial position",
    financialPositionDescription: "Net worth and the accounts that make it up",
    fromPreviousMonth: "from previous month",
    ledgerNotes: "Ledger notes",
    moneyIn: "Money in",
    moneyMovement: "Money movement",
    moneyMovementDescription: "Monthly income and spending by account category",
    moneyOut: "Money out",
    moveDown: "Move down",
    moveUp: "Move up",
    netWorthDescription: "Assets plus liabilities over the last 12 months",
    noMovement: "No money movement for this period.",
    noRecentActivity: "No recent transactions match the current filters.",
    partialPeriod: "partial",
    previousThreeMonthsAverage: "Previous 3 months average",
    recentActivity: "Recent activity",
    recentActivityCardDescription:
      "Newest transactions matching the current dashboard filters",
    recentActivityDescription: "Posting-aware activity from the journal",
    recentActivityError: "Recent transactions could not be loaded.",
    resetLayout: "Reset layout",
    showWidget: "Show module",
    tableView: "Show table",
    topSources: "Top sources",
    topSpending: "Top spending",
    transactionCleared: "Cleared",
    transactionMultiple: "Multiple postings",
    transactionOther: "Other",
    transactionPending: "Pending",
    transactionTransfer: "Transfer",
    unknownTransaction: "Untitled transaction",
    viewAll: "View all",
    viewAllAccounts: "View all accounts",
  },
  bg: {
    accountsDescription: "Текущи салда по сметки за активи и пасиви",
    allWidgetsHidden:
      "Всички модули на таблото са скрити. Персонализирайте таблото, за да възстановите модул.",
    chartView: "Покажи диаграма",
    customize: "Персонализиране",
    customizeDescription:
      "Изберете кои модули да се показват и ги подредете за тази книга.",
    financialPosition: "Финансова позиция",
    financialPositionDescription: "Нетна стойност и сметките, които я формират",
    fromPreviousMonth: "спрямо предходния месец",
    ledgerNotes: "Бележки към книгата",
    moneyIn: "Входящи средства",
    moneyMovement: "Движение на средства",
    moneyMovementDescription: "Месечни приходи и разходи по категория сметки",
    moneyOut: "Изходящи средства",
    moveDown: "Премести надолу",
    moveUp: "Премести нагоре",
    netWorthDescription: "Активи плюс пасиви през последните 12 месеца",
    noMovement: "Няма движение на средства за този период.",
    noRecentActivity: "Няма скорошни транзакции за текущите филтри.",
    partialPeriod: "непълен",
    previousThreeMonthsAverage: "Средно за предходните 3 месеца",
    recentActivity: "Скорошна активност",
    recentActivityCardDescription:
      "Най-новите транзакции според текущите филтри",
    recentActivityDescription:
      "Активност от журнала с отчитане на осчетоводяванията",
    recentActivityError: "Скорошните транзакции не можаха да бъдат заредени.",
    resetLayout: "Нулирай подредбата",
    showWidget: "Покажи модул",
    tableView: "Покажи таблица",
    topSources: "Водещи източници",
    topSpending: "Най-големи разходи",
    transactionCleared: "Приключена",
    transactionMultiple: "Няколко осчетоводявания",
    transactionOther: "Друго",
    transactionPending: "Предстояща",
    transactionTransfer: "Прехвърляне",
    unknownTransaction: "Транзакция без заглавие",
    viewAll: "Виж всички",
    viewAllAccounts: "Виж всички сметки",
  },
  ca: {
    accountsDescription: "Saldos actuals dels comptes d'actiu i passiu",
    allWidgetsHidden:
      "Tots els mòduls del tauler estan ocults. Personalitzeu el tauler per restaurar-ne un.",
    chartView: "Mostra el gràfic",
    customize: "Personalitza",
    customizeDescription:
      "Trieu quins mòduls es mostren i ordeneu-los per a aquest llibre.",
    financialPosition: "Posició financera",
    financialPositionDescription: "Patrimoni net i comptes que el formen",
    fromPreviousMonth: "respecte del mes anterior",
    ledgerNotes: "Notes del llibre",
    moneyIn: "Entrades",
    moneyMovement: "Moviment de diners",
    moneyMovementDescription:
      "Ingressos i despeses mensuals per categoria de compte",
    moneyOut: "Sortides",
    moveDown: "Mou avall",
    moveUp: "Mou amunt",
    netWorthDescription: "Actius més passius dels darrers 12 mesos",
    noMovement: "No hi ha moviment de diners en aquest període.",
    noRecentActivity:
      "Cap transacció recent coincideix amb els filtres actuals.",
    partialPeriod: "parcial",
    previousThreeMonthsAverage: "Mitjana dels 3 mesos anteriors",
    recentActivity: "Activitat recent",
    recentActivityCardDescription:
      "Transaccions més recents segons els filtres actuals",
    recentActivityDescription:
      "Activitat del diari conscient dels assentaments",
    recentActivityError: "No s'han pogut carregar les transaccions recents.",
    resetLayout: "Restableix la disposició",
    showWidget: "Mostra el mòdul",
    tableView: "Mostra la taula",
    topSources: "Fonts principals",
    topSpending: "Despeses principals",
    transactionCleared: "Liquidada",
    transactionMultiple: "Diversos assentaments",
    transactionOther: "Altres",
    transactionPending: "Pendent",
    transactionTransfer: "Transferència",
    unknownTransaction: "Transacció sense títol",
    viewAll: "Mostra-ho tot",
    viewAllAccounts: "Mostra tots els comptes",
  },
  de: {
    accountsDescription: "Aktuelle Salden der Aktiv- und Passivkonten",
    allWidgetsHidden:
      "Alle Dashboard-Module sind ausgeblendet. Passen Sie das Dashboard an, um eines wiederherzustellen.",
    chartView: "Diagramm anzeigen",
    customize: "Anpassen",
    customizeDescription:
      "Wählen Sie die sichtbaren Module und ordnen Sie sie für dieses Hauptbuch an.",
    financialPosition: "Finanzlage",
    financialPositionDescription: "Nettovermögen und die zugehörigen Konten",
    fromPreviousMonth: "gegenüber dem Vormonat",
    ledgerNotes: "Hauptbuchnotizen",
    moneyIn: "Geldeingang",
    moneyMovement: "Geldbewegung",
    moneyMovementDescription:
      "Monatliche Einnahmen und Ausgaben nach Kontokategorie",
    moneyOut: "Geldausgang",
    moveDown: "Nach unten verschieben",
    moveUp: "Nach oben verschieben",
    netWorthDescription: "Aktiva plus Passiva der letzten 12 Monate",
    noMovement: "Keine Geldbewegung in diesem Zeitraum.",
    noRecentActivity: "Keine aktuellen Transaktionen entsprechen den Filtern.",
    partialPeriod: "teilweise",
    previousThreeMonthsAverage: "Durchschnitt der vorherigen 3 Monate",
    recentActivity: "Letzte Aktivitäten",
    recentActivityCardDescription:
      "Neueste Transaktionen passend zu den Dashboard-Filtern",
    recentActivityDescription: "Buchungsbewusste Aktivitäten aus dem Journal",
    recentActivityError:
      "Die letzten Transaktionen konnten nicht geladen werden.",
    resetLayout: "Layout zurücksetzen",
    showWidget: "Modul anzeigen",
    tableView: "Tabelle anzeigen",
    topSources: "Größte Quellen",
    topSpending: "Größte Ausgaben",
    transactionCleared: "Gebucht",
    transactionMultiple: "Mehrere Buchungen",
    transactionOther: "Sonstige",
    transactionPending: "Ausstehend",
    transactionTransfer: "Umbuchung",
    unknownTransaction: "Transaktion ohne Titel",
    viewAll: "Alle anzeigen",
    viewAllAccounts: "Alle Konten anzeigen",
  },
  es: {
    accountsDescription: "Saldos actuales de las cuentas de activo y pasivo",
    allWidgetsHidden:
      "Todos los módulos del panel están ocultos. Personaliza el panel para restaurar uno.",
    chartView: "Mostrar gráfico",
    customize: "Personalizar",
    customizeDescription:
      "Elige qué módulos aparecen y ordénalos para este libro.",
    financialPosition: "Posición financiera",
    financialPositionDescription:
      "Patrimonio neto y las cuentas que lo componen",
    fromPreviousMonth: "desde el mes anterior",
    ledgerNotes: "Notas del libro",
    moneyIn: "Entradas de dinero",
    moneyMovement: "Movimiento de dinero",
    moneyMovementDescription:
      "Ingresos y gastos mensuales por categoría de cuenta",
    moneyOut: "Salidas de dinero",
    moveDown: "Mover abajo",
    moveUp: "Mover arriba",
    netWorthDescription: "Activos más pasivos durante los últimos 12 meses",
    noMovement: "No hay movimiento de dinero en este período.",
    noRecentActivity:
      "Ninguna transacción reciente coincide con los filtros actuales.",
    partialPeriod: "parcial",
    previousThreeMonthsAverage: "Promedio de los 3 meses anteriores",
    recentActivity: "Actividad reciente",
    recentActivityCardDescription:
      "Transacciones más recientes según los filtros actuales",
    recentActivityDescription: "Actividad del diario con detalle de asientos",
    recentActivityError: "No se pudieron cargar las transacciones recientes.",
    resetLayout: "Restablecer diseño",
    showWidget: "Mostrar módulo",
    tableView: "Mostrar tabla",
    topSources: "Fuentes principales",
    topSpending: "Mayores gastos",
    transactionCleared: "Confirmada",
    transactionMultiple: "Varios asientos",
    transactionOther: "Otra",
    transactionPending: "Pendiente",
    transactionTransfer: "Transferencia",
    unknownTransaction: "Transacción sin título",
    viewAll: "Ver todo",
    viewAllAccounts: "Ver todas las cuentas",
  },
  fa: {
    accountsDescription: "مانده فعلی حساب‌های دارایی و بدهی",
    allWidgetsHidden:
      "همه بخش‌های داشبورد پنهان هستند. برای بازگرداندن یک بخش، داشبورد را سفارشی کنید.",
    chartView: "نمایش نمودار",
    customize: "سفارشی‌سازی",
    customizeDescription:
      "بخش‌های قابل نمایش و ترتیب آن‌ها را برای این دفتر انتخاب کنید.",
    financialPosition: "وضعیت مالی",
    financialPositionDescription: "ارزش خالص و حساب‌های تشکیل‌دهنده آن",
    fromPreviousMonth: "نسبت به ماه قبل",
    ledgerNotes: "یادداشت‌های دفتر",
    moneyIn: "ورودی پول",
    moneyMovement: "گردش پول",
    moneyMovementDescription: "درآمد و هزینه ماهانه بر اساس دسته حساب",
    moneyOut: "خروجی پول",
    moveDown: "انتقال به پایین",
    moveUp: "انتقال به بالا",
    netWorthDescription: "دارایی‌ها به‌علاوه بدهی‌ها در ۱۲ ماه گذشته",
    noMovement: "در این دوره گردش پولی وجود ندارد.",
    noRecentActivity: "هیچ تراکنش اخیر با فیلترهای فعلی مطابقت ندارد.",
    partialPeriod: "ناقص",
    previousThreeMonthsAverage: "میانگین ۳ ماه قبل",
    recentActivity: "فعالیت اخیر",
    recentActivityCardDescription: "جدیدترین تراکنش‌های مطابق فیلترهای فعلی",
    recentActivityDescription: "فعالیت دفتر روزنامه با درنظرگرفتن ثبت‌ها",
    recentActivityError: "تراکنش‌های اخیر بارگیری نشدند.",
    resetLayout: "بازنشانی چیدمان",
    showWidget: "نمایش بخش",
    tableView: "نمایش جدول",
    topSources: "منابع اصلی",
    topSpending: "بیشترین هزینه‌ها",
    transactionCleared: "تسویه‌شده",
    transactionMultiple: "چند ثبت",
    transactionOther: "سایر",
    transactionPending: "در انتظار",
    transactionTransfer: "انتقال",
    unknownTransaction: "تراکنش بدون عنوان",
    viewAll: "نمایش همه",
    viewAllAccounts: "نمایش همه حساب‌ها",
  },
  fr: {
    accountsDescription: "Soldes actuels des comptes d'actif et de passif",
    allWidgetsHidden:
      "Tous les modules du tableau de bord sont masqués. Personnalisez-le pour en restaurer un.",
    chartView: "Afficher le graphique",
    customize: "Personnaliser",
    customizeDescription:
      "Choisissez les modules affichés et organisez-les pour ce grand livre.",
    financialPosition: "Situation financière",
    financialPositionDescription: "Patrimoine net et comptes qui le composent",
    fromPreviousMonth: "depuis le mois précédent",
    ledgerNotes: "Notes du grand livre",
    moneyIn: "Entrées d'argent",
    moneyMovement: "Mouvements d'argent",
    moneyMovementDescription:
      "Revenus et dépenses mensuels par catégorie de compte",
    moneyOut: "Sorties d'argent",
    moveDown: "Descendre",
    moveUp: "Monter",
    netWorthDescription: "Actifs plus passifs sur les 12 derniers mois",
    noMovement: "Aucun mouvement d'argent pour cette période.",
    noRecentActivity:
      "Aucune transaction récente ne correspond aux filtres actuels.",
    partialPeriod: "partiel",
    previousThreeMonthsAverage: "Moyenne des 3 mois précédents",
    recentActivity: "Activité récente",
    recentActivityCardDescription:
      "Transactions les plus récentes selon les filtres actuels",
    recentActivityDescription:
      "Activité du journal tenant compte des écritures",
    recentActivityError: "Impossible de charger les transactions récentes.",
    resetLayout: "Réinitialiser la disposition",
    showWidget: "Afficher le module",
    tableView: "Afficher le tableau",
    topSources: "Principales sources",
    topSpending: "Principales dépenses",
    transactionCleared: "Validée",
    transactionMultiple: "Plusieurs écritures",
    transactionOther: "Autre",
    transactionPending: "En attente",
    transactionTransfer: "Virement",
    unknownTransaction: "Transaction sans titre",
    viewAll: "Tout afficher",
    viewAllAccounts: "Afficher tous les comptes",
  },
  ja: {
    accountsDescription: "資産・負債勘定の現在残高",
    allWidgetsHidden:
      "すべてのダッシュボードモジュールが非表示です。カスタマイズして復元してください。",
    chartView: "チャートを表示",
    customize: "カスタマイズ",
    customizeDescription: "この元帳に表示するモジュールと順序を選択します。",
    financialPosition: "財務状況",
    financialPositionDescription: "純資産とその内訳となる勘定",
    fromPreviousMonth: "前月比",
    ledgerNotes: "元帳ノート",
    moneyIn: "入金",
    moneyMovement: "資金移動",
    moneyMovementDescription: "勘定カテゴリ別の月次収入と支出",
    moneyOut: "出金",
    moveDown: "下へ移動",
    moveUp: "上へ移動",
    netWorthDescription: "過去12か月の資産と負債の合計",
    noMovement: "この期間の資金移動はありません。",
    noRecentActivity: "現在のフィルターに一致する最近の取引はありません。",
    partialPeriod: "途中",
    previousThreeMonthsAverage: "過去3か月の平均",
    recentActivity: "最近のアクティビティ",
    recentActivityCardDescription: "現在のフィルターに一致する最新の取引",
    recentActivityDescription: "仕訳を考慮した元帳のアクティビティ",
    recentActivityError: "最近の取引を読み込めませんでした。",
    resetLayout: "レイアウトをリセット",
    showWidget: "モジュールを表示",
    tableView: "表を表示",
    topSources: "主な収入源",
    topSpending: "主な支出",
    transactionCleared: "確定済み",
    transactionMultiple: "複数の仕訳",
    transactionOther: "その他",
    transactionPending: "保留中",
    transactionTransfer: "振替",
    unknownTransaction: "無題の取引",
    viewAll: "すべて表示",
    viewAllAccounts: "すべての勘定を表示",
  },
  ko: {
    accountsDescription: "현재 자산 및 부채 계정 잔액",
    allWidgetsHidden:
      "모든 대시보드 모듈이 숨겨져 있습니다. 대시보드를 사용자 지정해 복원하세요.",
    chartView: "차트 보기",
    customize: "사용자 지정",
    customizeDescription: "이 원장에 표시할 모듈과 순서를 선택하세요.",
    financialPosition: "재무 상태",
    financialPositionDescription: "순자산과 이를 구성하는 계정",
    fromPreviousMonth: "전월 대비",
    ledgerNotes: "원장 메모",
    moneyIn: "유입",
    moneyMovement: "자금 흐름",
    moneyMovementDescription: "계정 범주별 월간 수입과 지출",
    moneyOut: "유출",
    moveDown: "아래로 이동",
    moveUp: "위로 이동",
    netWorthDescription: "지난 12개월의 자산과 부채 합계",
    noMovement: "이 기간에는 자금 이동이 없습니다.",
    noRecentActivity: "현재 필터와 일치하는 최근 거래가 없습니다.",
    partialPeriod: "진행 중",
    previousThreeMonthsAverage: "이전 3개월 평균",
    recentActivity: "최근 활동",
    recentActivityCardDescription: "현재 필터와 일치하는 최신 거래",
    recentActivityDescription: "분개를 반영한 원장 활동",
    recentActivityError: "최근 거래를 불러오지 못했습니다.",
    resetLayout: "레이아웃 초기화",
    showWidget: "모듈 표시",
    tableView: "표 보기",
    topSources: "주요 수입원",
    topSpending: "주요 지출",
    transactionCleared: "완료",
    transactionMultiple: "여러 분개",
    transactionOther: "기타",
    transactionPending: "대기 중",
    transactionTransfer: "이체",
    unknownTransaction: "제목 없는 거래",
    viewAll: "모두 보기",
    viewAllAccounts: "모든 계정 보기",
  },
  nl: {
    accountsDescription: "Huidige saldi van activa- en passivarekeningen",
    allWidgetsHidden:
      "Alle dashboardmodules zijn verborgen. Pas het dashboard aan om er een te herstellen.",
    chartView: "Grafiek tonen",
    customize: "Aanpassen",
    customizeDescription:
      "Kies welke modules verschijnen en rangschik ze voor dit grootboek.",
    financialPosition: "Financiële positie",
    financialPositionDescription: "Nettovermogen en de bijbehorende rekeningen",
    fromPreviousMonth: "ten opzichte van vorige maand",
    ledgerNotes: "Grootboeknotities",
    moneyIn: "Geld in",
    moneyMovement: "Geldbeweging",
    moneyMovementDescription:
      "Maandelijkse inkomsten en uitgaven per rekeningcategorie",
    moneyOut: "Geld uit",
    moveDown: "Omlaag verplaatsen",
    moveUp: "Omhoog verplaatsen",
    netWorthDescription: "Activa plus passiva over de afgelopen 12 maanden",
    noMovement: "Geen geldbeweging in deze periode.",
    noRecentActivity: "Geen recente transacties passen bij de huidige filters.",
    partialPeriod: "gedeeltelijk",
    previousThreeMonthsAverage: "Gemiddelde van de vorige 3 maanden",
    recentActivity: "Recente activiteit",
    recentActivityCardDescription:
      "Nieuwste transacties volgens de huidige filters",
    recentActivityDescription: "Journaalactiviteit met aandacht voor boekingen",
    recentActivityError: "Recente transacties konden niet worden geladen.",
    resetLayout: "Indeling herstellen",
    showWidget: "Module tonen",
    tableView: "Tabel tonen",
    topSources: "Belangrijkste bronnen",
    topSpending: "Grootste uitgaven",
    transactionCleared: "Verwerkt",
    transactionMultiple: "Meerdere boekingen",
    transactionOther: "Overig",
    transactionPending: "In behandeling",
    transactionTransfer: "Overboeking",
    unknownTransaction: "Transactie zonder titel",
    viewAll: "Alles bekijken",
    viewAllAccounts: "Alle rekeningen bekijken",
  },
  pt: {
    accountsDescription: "Saldos atuais das contas de ativos e passivos",
    allWidgetsHidden:
      "Todos os módulos do painel estão ocultos. Personalize o painel para restaurar um.",
    chartView: "Mostrar gráfico",
    customize: "Personalizar",
    customizeDescription:
      "Escolha os módulos exibidos e organize-os para este livro-razão.",
    financialPosition: "Posição financeira",
    financialPositionDescription:
      "Patrimônio líquido e as contas que o compõem",
    fromPreviousMonth: "desde o mês anterior",
    ledgerNotes: "Notas do livro-razão",
    moneyIn: "Entradas",
    moneyMovement: "Movimentação de dinheiro",
    moneyMovementDescription:
      "Receitas e despesas mensais por categoria de conta",
    moneyOut: "Saídas",
    moveDown: "Mover para baixo",
    moveUp: "Mover para cima",
    netWorthDescription: "Ativos mais passivos nos últimos 12 meses",
    noMovement: "Não houve movimentação de dinheiro neste período.",
    noRecentActivity:
      "Nenhuma transação recente corresponde aos filtros atuais.",
    partialPeriod: "parcial",
    previousThreeMonthsAverage: "Média dos 3 meses anteriores",
    recentActivity: "Atividade recente",
    recentActivityCardDescription:
      "Transações mais recentes segundo os filtros atuais",
    recentActivityDescription:
      "Atividade do diário considerando os lançamentos",
    recentActivityError: "Não foi possível carregar as transações recentes.",
    resetLayout: "Redefinir layout",
    showWidget: "Mostrar módulo",
    tableView: "Mostrar tabela",
    topSources: "Principais fontes",
    topSpending: "Maiores despesas",
    transactionCleared: "Compensada",
    transactionMultiple: "Vários lançamentos",
    transactionOther: "Outra",
    transactionPending: "Pendente",
    transactionTransfer: "Transferência",
    unknownTransaction: "Transação sem título",
    viewAll: "Ver tudo",
    viewAllAccounts: "Ver todas as contas",
  },
  ru: {
    accountsDescription: "Текущие остатки по счетам активов и обязательств",
    allWidgetsHidden:
      "Все модули панели скрыты. Настройте панель, чтобы вернуть нужный модуль.",
    chartView: "Показать график",
    customize: "Настроить",
    customizeDescription:
      "Выберите отображаемые модули и расположите их для этой книги.",
    financialPosition: "Финансовое положение",
    financialPositionDescription: "Чистая стоимость и составляющие её счета",
    fromPreviousMonth: "по сравнению с прошлым месяцем",
    ledgerNotes: "Заметки книги",
    moneyIn: "Поступления",
    moneyMovement: "Движение денег",
    moneyMovementDescription:
      "Ежемесячные доходы и расходы по категориям счетов",
    moneyOut: "Списания",
    moveDown: "Переместить вниз",
    moveUp: "Переместить вверх",
    netWorthDescription: "Активы плюс обязательства за последние 12 месяцев",
    noMovement: "В этом периоде нет движения денег.",
    noRecentActivity:
      "Нет недавних транзакций, соответствующих текущим фильтрам.",
    partialPeriod: "неполный",
    previousThreeMonthsAverage: "Среднее за предыдущие 3 месяца",
    recentActivity: "Последние операции",
    recentActivityCardDescription: "Новые транзакции по текущим фильтрам",
    recentActivityDescription: "Операции журнала с учётом проводок",
    recentActivityError: "Не удалось загрузить последние транзакции.",
    resetLayout: "Сбросить макет",
    showWidget: "Показать модуль",
    tableView: "Показать таблицу",
    topSources: "Основные источники",
    topSpending: "Основные расходы",
    transactionCleared: "Проведена",
    transactionMultiple: "Несколько проводок",
    transactionOther: "Другая",
    transactionPending: "Ожидает",
    transactionTransfer: "Перевод",
    unknownTransaction: "Транзакция без названия",
    viewAll: "Показать все",
    viewAllAccounts: "Показать все счета",
  },
  sk: {
    accountsDescription: "Aktuálne zostatky účtov aktív a pasív",
    allWidgetsHidden:
      "Všetky moduly nástenky sú skryté. Prispôsobte nástenku a niektorý obnovte.",
    chartView: "Zobraziť graf",
    customize: "Prispôsobiť",
    customizeDescription:
      "Vyberte zobrazené moduly a usporiadajte ich pre túto účtovnú knihu.",
    financialPosition: "Finančná pozícia",
    financialPositionDescription: "Čistá hodnota a účty, ktoré ju tvoria",
    fromPreviousMonth: "oproti predchádzajúcemu mesiacu",
    ledgerNotes: "Poznámky ku knihe",
    moneyIn: "Príjmy",
    moneyMovement: "Pohyb peňazí",
    moneyMovementDescription: "Mesačné príjmy a výdavky podľa kategórie účtu",
    moneyOut: "Výdavky",
    moveDown: "Posunúť nadol",
    moveUp: "Posunúť nahor",
    netWorthDescription: "Aktíva plus pasíva za posledných 12 mesiacov",
    noMovement: "V tomto období nebol žiadny pohyb peňazí.",
    noRecentActivity:
      "Aktuálnym filtrom nezodpovedajú žiadne nedávne transakcie.",
    partialPeriod: "čiastočné",
    previousThreeMonthsAverage: "Priemer predchádzajúcich 3 mesiacov",
    recentActivity: "Nedávna aktivita",
    recentActivityCardDescription:
      "Najnovšie transakcie podľa aktuálnych filtrov",
    recentActivityDescription: "Aktivita denníka so zohľadnením zápisov",
    recentActivityError: "Nedávne transakcie sa nepodarilo načítať.",
    resetLayout: "Obnoviť rozloženie",
    showWidget: "Zobraziť modul",
    tableView: "Zobraziť tabuľku",
    topSources: "Hlavné zdroje",
    topSpending: "Najväčšie výdavky",
    transactionCleared: "Zaúčtovaná",
    transactionMultiple: "Viacero zápisov",
    transactionOther: "Iná",
    transactionPending: "Čakajúca",
    transactionTransfer: "Prevod",
    unknownTransaction: "Transakcia bez názvu",
    viewAll: "Zobraziť všetko",
    viewAllAccounts: "Zobraziť všetky účty",
  },
  uk: {
    accountsDescription: "Поточні залишки на рахунках активів і зобов'язань",
    allWidgetsHidden:
      "Усі модулі панелі приховано. Налаштуйте панель, щоб відновити модуль.",
    chartView: "Показати графік",
    customize: "Налаштувати",
    customizeDescription:
      "Виберіть модулі для показу та впорядкуйте їх для цієї книги.",
    financialPosition: "Фінансовий стан",
    financialPositionDescription: "Чиста вартість і рахунки, що її формують",
    fromPreviousMonth: "порівняно з попереднім місяцем",
    ledgerNotes: "Нотатки книги",
    moneyIn: "Надходження",
    moneyMovement: "Рух коштів",
    moneyMovementDescription:
      "Щомісячні доходи й витрати за категоріями рахунків",
    moneyOut: "Витрати",
    moveDown: "Перемістити вниз",
    moveUp: "Перемістити вгору",
    netWorthDescription: "Активи плюс зобов'язання за останні 12 місяців",
    noMovement: "У цьому періоді руху коштів немає.",
    noRecentActivity: "Немає недавніх транзакцій за поточними фільтрами.",
    partialPeriod: "неповний",
    previousThreeMonthsAverage: "Середнє за попередні 3 місяці",
    recentActivity: "Остання активність",
    recentActivityCardDescription:
      "Найновіші транзакції за поточними фільтрами",
    recentActivityDescription: "Активність журналу з урахуванням проведень",
    recentActivityError: "Не вдалося завантажити останні транзакції.",
    resetLayout: "Скинути розкладку",
    showWidget: "Показати модуль",
    tableView: "Показати таблицю",
    topSources: "Основні джерела",
    topSpending: "Найбільші витрати",
    transactionCleared: "Проведена",
    transactionMultiple: "Кілька проведень",
    transactionOther: "Інша",
    transactionPending: "Очікує",
    transactionTransfer: "Переказ",
    unknownTransaction: "Транзакція без назви",
    viewAll: "Показати все",
    viewAllAccounts: "Показати всі рахунки",
  },
  zh: {
    accountsDescription: "当前资产和负债账户余额",
    allWidgetsHidden: "所有仪表板模块均已隐藏。请自定义仪表板以恢复模块。",
    chartView: "显示图表",
    customize: "自定义",
    customizeDescription: "选择要显示的模块，并为此账本调整顺序。",
    financialPosition: "财务状况",
    financialPositionDescription: "净资产及其组成账户",
    fromPreviousMonth: "较上月",
    ledgerNotes: "账本备注",
    moneyIn: "资金流入",
    moneyMovement: "资金流动",
    moneyMovementDescription: "按账户类别显示每月收入和支出",
    moneyOut: "资金流出",
    moveDown: "下移",
    moveUp: "上移",
    netWorthDescription: "过去 12 个月的资产与负债之和",
    noMovement: "此期间没有资金流动。",
    noRecentActivity: "没有符合当前筛选条件的近期交易。",
    partialPeriod: "未完整",
    previousThreeMonthsAverage: "前 3 个月平均值",
    recentActivity: "近期活动",
    recentActivityCardDescription: "符合当前筛选条件的最新交易",
    recentActivityDescription: "考虑分录的账本活动",
    recentActivityError: "无法加载近期交易。",
    resetLayout: "重置布局",
    showWidget: "显示模块",
    tableView: "显示表格",
    topSources: "主要来源",
    topSpending: "主要支出",
    transactionCleared: "已入账",
    transactionMultiple: "多笔分录",
    transactionOther: "其他",
    transactionPending: "待处理",
    transactionTransfer: "转账",
    unknownTransaction: "未命名交易",
    viewAll: "查看全部",
    viewAllAccounts: "查看所有账户",
  },
};

function buildDashboardTranslations(
  language: DashboardLanguage,
): Record<string, DashboardTranslationEntry> {
  return Object.fromEntries(
    (Object.keys(descriptions) as DashboardTranslationKey[]).map((key) => [
      `page.overview.${key}`,
      {
        message: messages[language][key],
        description: descriptions[key],
      },
    ]),
  );
}

export const dashboardOverviewTranslations: Record<
  DashboardLanguage,
  Record<string, DashboardTranslationEntry>
> = {
  bg: buildDashboardTranslations("bg"),
  ca: buildDashboardTranslations("ca"),
  de: buildDashboardTranslations("de"),
  en: buildDashboardTranslations("en"),
  es: buildDashboardTranslations("es"),
  fa: buildDashboardTranslations("fa"),
  fr: buildDashboardTranslations("fr"),
  ja: buildDashboardTranslations("ja"),
  ko: buildDashboardTranslations("ko"),
  nl: buildDashboardTranslations("nl"),
  pt: buildDashboardTranslations("pt"),
  ru: buildDashboardTranslations("ru"),
  sk: buildDashboardTranslations("sk"),
  uk: buildDashboardTranslations("uk"),
  zh: buildDashboardTranslations("zh"),
};
